// src/components/AgoraCall.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import {
  AgoraRTCProvider,
  useJoin,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  usePublish,
  useRemoteUsers,
} from "agora-rtc-react";
import Draggable from "react-draggable";

const APP_ID = process.env.REACT_APP_AGORA_APP_ID;
// const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
const client = AgoraRTC.createClient({ 
  mode: "rtc", 
  codec: "h264"  // ← THIS IS MANDATORY FOR MOBILE
});

// Enable dual stream so mobile gets low-res fallback
// client.enableDualStream();

function AgoraCallInner({
  channel,
  token,
  uid,
  callType = "video",
  astroName = "Psychic",
  astroImage = "https://via.placeholder.com/150",
  userName = "User",
  userImage = "https://via.placeholder.com/100",
  endTimeUTC,
}) {
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(callType === "video");
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [isRemoteMuted, setIsRemoteMuted] = useState(false);
  const [countdown, setCountdown] = useState("00:00");
  const [isConnected, setIsConnected] = useState(false);

  const { localMicrophoneTrack } = useLocalMicrophoneTrack();
  const { localCameraTrack } = useLocalCameraTrack(callType === "video" && isCameraOn);
  const remoteUsers = useRemoteUsers();
  const remoteUser = remoteUsers[0]; // This is the CUSTOMER

  useJoin({ appid: APP_ID, channel, token, uid }, true);
  usePublish([localMicrophoneTrack, callType === "video" && isCameraOn ? localCameraTrack : null].filter(Boolean));

  // Timer
  useEffect(() => {
    if (!endTimeUTC) return;
    const timer = setInterval(() => {
      const diff = Math.max(0, Math.floor((new Date(endTimeUTC) - Date.now()) / 1000));
      const m = String(Math.floor(diff / 60)).padStart(2, "0");
      const s = String(diff % 60).padStart(2, "0");
      setCountdown(`${m}:${s}`);
      if (diff === 0) leaveCall();
    }, 1000);
    return () => clearInterval(timer);
  }, [endTimeUTC]);

  // Remote Events (Customer joins)
  useEffect(() => {
    const handlePublished = async (user, mediaType) => {
      await client.subscribe(user, mediaType);
      if (mediaType === "audio") {
        user.audioTrack?.play();
        setIsRemoteMuted(false);
      }
    };
    client.on("user-published", handlePublished);
    client.on("user-unpublished", () => setIsRemoteMuted(true));
    client.on("user-joined", () => setIsConnected(true));
    client.on("user-left", () => setIsConnected(false));
    return () => client.removeAllListeners();
  }, []);

  const toggleMute = async () => {
    await localMicrophoneTrack?.setEnabled(!isMuted);
    setIsMuted(!isMuted);
  };

  const toggleCamera = async () => {
    await localCameraTrack?.setEnabled(!isCameraOn);
    setIsCameraOn(!isCameraOn);
  };

  const toggleSpeaker = () => {
    remoteUsers.forEach(u => u.audioTrack?.setVolume(isSpeakerOn ? 0 : 100));
    setIsSpeakerOn(!isSpeakerOn);
  };

  const leaveCall = useCallback(() => {
    localMicrophoneTrack?.close();
    localCameraTrack?.close();
    client.leave();
    window.history.back();
  }, [localMicrophoneTrack, localCameraTrack]);

  const VideoPlayer = ({ track, muted = false }) => {
    const ref = useRef(null);
    useEffect(() => {
      if (track && ref.current) track.play(ref.current);
      return () => track?.stop();
    }, [track]);
    return (
      <video
        ref={ref}
        className="w-100 h-100 object-fit-cover"
        autoPlay
        playsInline
        muted={muted}
      />
    );
  };

  const isVideoCall = callType === "video";
  const showRemoteVideo = isVideoCall && remoteUser?.videoTrack;

  return (
    <div className="position-fixed top-0 start-0 w-100 h-100 text-white overflow-hidden">
      {/* Background */}
      <div className="position-absolute top-0 start-0 w-100 h-100" style={{
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      }}></div>

      {/* Remote (Customer) Video OR Profile */}
      <div className="position-absolute top-0 start-0 w-100 h-100">
        {showRemoteVideo ? (
          <VideoPlayer track={remoteUser.videoTrack} />
        ) : (
          <div className="d-flex flex-column align-items-center justify-content-center h-100 px-4">
            <div className="text-center">
              {/* Pulsing Ring */}
              <div className="position-relative d-inline-block mb-5">
                <div className="position-absolute top-50 start-50 translate-middle">
                  <div className="spinner-border text-white" style={{ width: "4rem", height: "4rem" }}></div>
                </div>
                <img
                  src={userImage}
                  alt={userName}
                  className="rounded-circle border border-5 border-white shadow-lg"
                  width="130"
                  height="130"
                />
              </div>

              <h2 className="fw-bold mb-2">{userName || "User"}</h2>
              <p className="fs-5 opacity-90">
                {isConnected ? "Customer is joining..." : "Waiting for customer..."}
              </p>

              <div className="mt-4 d-inline-block bg-white text-dark rounded-pill px-5 py-3 fs-4 fw-bold shadow-lg">
                {countdown}
              </div>
            </div>

            {/* Your (Psychic) Profile at Bottom */}
            <div className="position-absolute bottom-0 start-50 translate-middle-x mb-5 text-center">
              <img
                src={astroImage}
                alt={astroName}
                className="rounded-circle border border-4 border-white shadow-lg"
                width="90"
                height="90"
              />
              <p className="mt-2 fw-bold">{astroName} (You)</p>
            </div>
          </div>
        )}
      </div>

      {/* Remote (Customer) Muted */}
      {isRemoteMuted && (
        <div className="position-absolute top-10 start-50 translate-middle-x bg-danger px-4 py-2 rounded-pill shadow-lg z-10">
          {userName} is muted
        </div>
      )}

      {/* Your (Psychic) Draggable Video */}
      {isVideoCall && (
        <Draggable bounds="parent">
          <div
            className="position-absolute bg-dark rounded-4 overflow-hidden shadow-2xl border border-white z-10"
            style={{
              width: "150px",
              height: "200px",
              bottom: "140px",
              right: "20px",
              cursor: "move",
              display: showRemoteVideo ? "block" : "none"
            }}
          >
            {isCameraOn && localCameraTrack ? (
              <VideoPlayer track={localCameraTrack} muted />
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center h-100 text-white bg-gradient bg-secondary">
                <img src={astroImage} alt={astroName} className="rounded-circle mb-2" width="80" />
                <small>{astroName} (You)</small>
                <small className="text-warning">Camera Off</small>
              </div>
            )}
          </div>
        </Draggable>
      )}

      {/* Bottom Controls */}
      <div className="position-absolute bottom-0 start-0 end-0 bg-black bg-opacity-90 py-4 border-top border-white border-opacity-20">
        <div className="d-flex justify-content-center align-items-center gap-4">
          <button onClick={toggleSpeaker} className={`btn btn-lg rounded-circle ${isSpeakerOn ? "btn-light" : "btn-danger"} shadow`}>
            <i className={`bi ${isSpeakerOn ? "bi-volume-up-fill" : "bi-volume-mute-fill"} fs-3`}></i>
          </button>

          <button onClick={toggleMute} className={`btn btn-lg rounded-circle ${isMuted ? "btn-danger" : "btn-light"} shadow`}>
            <i className={`bi ${isMuted ? "bi-mic-mute-fill" : "bi-mic-fill"} fs-3`}></i>
          </button>

          {isVideoCall && (
            <button onClick={toggleCamera} className={`btn btn-lg rounded-circle ${!isCameraOn ? "btn-danger" : "btn-light"} shadow`}>
              <i className={`bi ${isCameraOn ? "bi-camera-video-fill" : "bi-camera-video-off-fill"} fs-3`}></i>
            </button>
          )}

          <button onClick={leaveCall} className="btn btn-danger btn-lg rounded-circle shadow-lg">
            <i className="bi bi-telephone-x-fill fs-3"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AgoraCall(props) {
  return (
    <AgoraRTCProvider client={client}>
      <AgoraCallInner {...props} />
    </AgoraRTCProvider>
  );
}